import { qk, useTemplateMappings, useTemplates } from '@restaurant/api-client/react';
import {
  countPlaceholders,
  NOTIFY_STATUSES,
  ORDER_STATUS_LABEL,
  TEMPLATE_VARIABLES,
  type CreateTemplateInput,
  type TemplateDto,
} from '@restaurant/shared';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/input';
import { EmptyState, PageHeader, Select, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toastError } from '@/lib/errors';

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
  APPROVED: 'success',
  PENDING: 'warning',
  REJECTED: 'destructive',
  PAUSED: 'destructive',
  DISABLED: 'muted',
};
const bodyOf = (t: TemplateDto) => ((t.components ?? []) as { type: string; text?: string }[]).find((c) => c.type.toUpperCase() === 'BODY')?.text ?? '';

export function TemplatesPage() {
  const { data: templates = [], isLoading } = useTemplates();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const isOwner = user?.role === 'owner';

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.templates.sync();
      toast.success(`Synced ${r.total} template(s) from WhatsApp`);
      await qc.invalidateQueries({ queryKey: qk.templates });
    } catch (err) {
      toastError(err);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Message templates"
        description="Approved templates can be sent anytime, including after the 24-hour window closes."
        actions={
          <>
            <Button variant="outline" onClick={() => void sync()} disabled={syncing}>{syncing ? <Spinner /> : <RefreshCw />} Sync</Button>
            {isOwner ? <Button onClick={() => setCreating(true)}><Plus /> New template</Button> : null}
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto p-4 md:p-6">
        <div className="mx-auto grid max-w-5xl gap-6">
          {isOwner ? <StatusMapping templates={templates} /> : null}
          {isLoading ? null : templates.length === 0 ? (
            <EmptyState icon={<FileText />} title="No templates yet" description="Create one (e.g. order_ready) and submit it to WhatsApp for approval." />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {templates.map((t) => (
                <Card key={t.id}>
                  <CardHeader className="flex-row items-start justify-between gap-2">
                    <div>
                      <CardTitle className="font-mono">{t.name}</CardTitle>
                      <CardDescription>{t.language} · {t.category.toLowerCase()}</CardDescription>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant={STATUS_VARIANT[t.status] ?? 'muted'}>{t.status.toLowerCase()}</Badge>
                      {isOwner ? (
                        <Button size="icon" variant="ghost" aria-label="Delete template" onClick={() => void api.templates.remove(t.name).then(() => qc.invalidateQueries({ queryKey: qk.templates })).catch(toastError)}>
                          <Trash2 />
                        </Button>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="whitespace-pre-wrap rounded-lg bg-bubble-out p-3 text-sm">{bodyOf(t)}</p>
                    {t.rejectedReason ? <p className="mt-2 text-xs text-destructive">Rejected: {t.rejectedReason}</p> : null}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      {creating ? <CreateTemplateDialog onClose={() => setCreating(false)} onCreated={() => void qc.invalidateQueries({ queryKey: qk.templates })} /> : null}
    </div>
  );
}

function CreateTemplateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [f, setF] = useState<CreateTemplateInput>({ name: '', language: 'en_US', category: 'UTILITY', headerFormat: 'TEXT', body: '', bodyExamples: [], buttons: [], headerText: '', footer: '' });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const quickReplies = f.buttons.filter((b) => b.type === 'QUICK_REPLY');
  const setButtons = (buttons: CreateTemplateInput['buttons']) => setF({ ...f, buttons });
  const uploadSample = async (file: File | undefined) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024) return toast.error('Use a JPEG or PNG up to 5 MB');
    setUploading(true);
    try {
      const { url } = await api.menu.uploadImage(file, file.name);
      setF({ ...f, headerImageUrl: url });
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };
  const vars = countPlaceholders(f.body);
  const preview = f.body.replace(/\{\{(\d+)\}\}/g, (_, n: string) => f.bodyExamples[Number(n) - 1] || `{{${n}}}`);

  const submit = async () => {
    setBusy(true);
    try {
      await api.templates.create({
        ...f,
        headerText: f.headerFormat === 'TEXT' ? f.headerText || undefined : undefined,
        headerImageUrl: f.headerFormat === 'IMAGE' ? f.headerImageUrl : undefined,
        footer: f.footer || undefined,
        bodyExamples: f.bodyExamples.slice(0, vars),
      });
      toast.success('Submitted to WhatsApp for review', { description: 'Approval usually takes minutes to a few hours.' });
      onCreated();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title="New template" description="Use {{1}}, {{2}}… for variables. Give an example for each." side="right">
        <div className="grid gap-4">
          <Field label="Name" hint="lowercase_with_underscores, e.g. order_ready">
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} className="font-mono" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v as CreateTemplateInput['category'] })} options={[{ value: 'UTILITY', label: 'Utility (order updates)' }, { value: 'MARKETING', label: 'Marketing' }]} />
            </Field>
            <Field label="Language"><Input value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} /></Field>
          </div>
          <Field label="Header" hint={f.headerFormat === 'IMAGE' ? 'Each promotion sends its own picture; this sample is only for Meta review.' : 'Optional'}>
            <div className="grid gap-2">
              <Select
                value={f.headerFormat}
                onValueChange={(v) => setF({ ...f, headerFormat: v as 'TEXT' | 'IMAGE' })}
                options={[{ value: 'TEXT', label: 'Text (or none)' }, { value: 'IMAGE', label: 'Picture' }]}
              />
              {f.headerFormat === 'TEXT' ? (
                <Input value={f.headerText ?? ''} maxLength={60} onChange={(e) => setF({ ...f, headerText: e.target.value })} placeholder="Leave empty for no header" />
              ) : (
                <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed p-2 text-sm text-muted-foreground hover:bg-accent">
                  {f.headerImageUrl ? <img src={f.headerImageUrl} alt="" className="size-14 rounded object-cover" /> : null}
                  {uploading ? <Spinner /> : null}
                  <span>{f.headerImageUrl ? 'Change sample picture' : 'Upload a sample picture (JPEG/PNG)'}</span>
                  <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => void uploadSample(e.target.files?.[0])} />
                </label>
              )}
            </div>
          </Field>
          <Field label="Body"><Textarea rows={4} value={f.body} maxLength={1024} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder="Hi {{1}}, your order {{2}} is ready for pickup." /></Field>
          {Array.from({ length: vars }, (_, i) => (
            <Field key={i} label={`Example for {{${i + 1}}}`}>
              <Input value={f.bodyExamples[i] ?? ''} onChange={(e) => setF({ ...f, bodyExamples: Object.assign([...f.bodyExamples], { [i]: e.target.value }) })} />
            </Field>
          ))}
          <Field label="Footer (optional)"><Input value={f.footer ?? ''} maxLength={60} onChange={(e) => setF({ ...f, footer: e.target.value })} /></Field>
          <Field label="Quick-reply buttons (optional)" hint='An "Order now" button lets customers start an order from a promotion with one tap.'>
            <div className="grid gap-2">
              {quickReplies.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={b.text} maxLength={25} onChange={(e) => setButtons(quickReplies.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))} />
                  <Button size="icon" variant="ghost" aria-label="Remove button" onClick={() => setButtons(quickReplies.filter((_, j) => j !== i))}>✕</Button>
                </div>
              ))}
              {quickReplies.length < 3 ? (
                <div className="flex flex-wrap gap-2">
                  {!quickReplies.some((b) => /order/i.test(b.text)) ? (
                    <Button size="sm" variant="outline" onClick={() => setButtons([...quickReplies, { type: 'QUICK_REPLY', text: 'Order now' }])}>+ "Order now"</Button>
                  ) : null}
                  <Button size="sm" variant="ghost" onClick={() => setButtons([...quickReplies, { type: 'QUICK_REPLY', text: '' }])}>+ Other button</Button>
                </div>
              ) : null}
            </div>
          </Field>
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Preview</p>
            <div className="rounded-lg bg-chat-bg p-3">
              <div className="max-w-xs rounded-xl rounded-tl-sm bg-bubble-in p-3 text-sm shadow-xs">
                {f.headerFormat === 'IMAGE' && f.headerImageUrl ? <img src={f.headerImageUrl} alt="" className="mb-2 max-h-40 w-full rounded-lg object-cover" /> : null}
                {f.headerFormat === 'TEXT' && f.headerText ? <p className="mb-1 font-semibold">{f.headerText}</p> : null}
                <p className="whitespace-pre-wrap">{preview || 'Your message…'}</p>
                {f.footer ? <p className="mt-1 text-xs text-muted-foreground">{f.footer}</p> : null}
              </div>
              {quickReplies.map((b, i) => <div key={i} className="mt-1 max-w-xs rounded-xl bg-bubble-in py-1.5 text-center text-sm font-medium text-sky-600 shadow-xs">{b.text || 'Button'}</div>)}
            </div>
          </div>
          <Button onClick={() => void submit()} disabled={busy || uploading || !f.name || !f.body || f.bodyExamples.slice(0, vars).filter(Boolean).length < vars || (f.headerFormat === 'IMAGE' && !f.headerImageUrl) || quickReplies.some((b) => !b.text.trim())}>
            {busy ? <Spinner /> : null} Submit for approval
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Which approved template to send per order status when the customer's 24h window is closed. */
function StatusMapping({ templates }: { templates: TemplateDto[] }) {
  const { data: mappings = [] } = useTemplateMappings();
  const qc = useQueryClient();
  const approved = templates.filter((t) => t.status === 'APPROVED');
  const [edit, setEdit] = useState<{ status: string; key?: string; vars: string[] } | null>(null);
  const tpl = edit?.key ? approved.find((t) => `${t.name}|${t.language}` === edit.key) : undefined;
  const count = tpl ? countPlaceholders(bodyOf(tpl)) : 0;

  const save = async () => {
    if (!edit || !tpl) return;
    try {
      await api.templates.setMapping({ status: edit.status as (typeof NOTIFY_STATUSES)[number], templateName: tpl.name, language: tpl.language, bodyVariables: edit.vars.slice(0, count) as never });
      await qc.invalidateQueries({ queryKey: qk.mappings });
      toast.success('Mapping saved');
      setEdit(null);
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Order notifications outside the 24-hour window</CardTitle>
        <CardDescription>Inside the window, customers get the free-form messages from Settings. After it closes, WhatsApp only allows approved templates.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {NOTIFY_STATUSES.map((s) => {
          const m = mappings.find((x) => x.status === s);
          return (
            <div key={s} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2">
              <span className="text-sm font-medium">{ORDER_STATUS_LABEL[s]}</span>
              <span className="flex items-center gap-2 text-sm">
                {m ? <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{m.templateName}</code> : <span className="text-muted-foreground">Not mapped: customer won't be notified</span>}
                <Button size="sm" variant="outline" onClick={() => setEdit({ status: s, key: m ? `${m.templateName}|${m.language}` : undefined, vars: m?.bodyVariables ?? [] })}>{m ? 'Change' : 'Map'}</Button>
                {m ? <Button size="sm" variant="ghost" onClick={() => void api.templates.removeMapping(s).then(() => qc.invalidateQueries({ queryKey: qk.mappings })).catch(toastError)}>Remove</Button> : null}
              </span>
            </div>
          );
        })}
      </CardContent>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        {edit ? (
          <DialogContent title={`Template for "${ORDER_STATUS_LABEL[edit.status as (typeof NOTIFY_STATUSES)[number]]}"`}>
            {approved.length === 0 ? <p className="text-sm text-muted-foreground">No approved templates yet.</p> : (
              <div className="grid gap-3">
                <Field label="Template">
                  <Select value={edit.key} onValueChange={(v) => setEdit({ ...edit, key: v, vars: [] })} placeholder="Choose" options={approved.map((t) => ({ value: `${t.name}|${t.language}`, label: `${t.name} (${t.language})` }))} />
                </Field>
                {tpl ? <p className="whitespace-pre-wrap rounded-md bg-muted p-2 text-sm">{bodyOf(tpl)}</p> : null}
                {Array.from({ length: count }, (_, i) => (
                  <Field key={i} label={`{{${i + 1}}} is filled with`}>
                    <Select value={edit.vars[i]} onValueChange={(v) => setEdit({ ...edit, vars: Object.assign([...edit.vars], { [i]: v }) })} placeholder="Choose a value" options={TEMPLATE_VARIABLES.map((v) => ({ value: v, label: v }))} />
                  </Field>
                ))}
                <Button onClick={() => void save()} disabled={!tpl || edit.vars.slice(0, count).filter(Boolean).length < count}>Save</Button>
              </div>
            )}
          </DialogContent>
        ) : null}
      </Dialog>
    </Card>
  );
}
